// BL-177 Step 3: the browser's side of scheduled send. Talks to the staff-schedule-message Edge Function (create / edit / cancel /
// send now) and reads scheduled messages straight from the table (row-level security shows staff their own, admins the company's).
import { supabaseClient } from '../config/supabase';
import { buildStaffReplyPayload, readFunctionError, validateStaffReply } from './staffReply';

export const SCHEDULE_FUNCTION = 'staff-schedule-message';
/** Fired on window after any change, so open lists and threads reload. */
export const SCHEDULED_CHANGED_EVENT = 'ia:scheduled-changed';

function announceChange() {
    try { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(SCHEDULED_CHANGED_EVENT)); } catch { /* nothing is listening */ }
}

async function invoke(client, body) {
    const { data, error } = await client.functions.invoke(SCHEDULE_FUNCTION, { body });
    if (error) {
        const parsed = await readFunctionError(error);
        throw new Error(parsed.message);
    }
    if (data?.error) throw new Error(typeof data.error === 'string' ? data.error : 'That could not be done.');
    announceChange();
    return data;
}

/**
 * Schedule the message in the composer. Same inputs as sendStaffReply (so the same validation and payload apply), plus
 * `scheduledFor` (an ISO instant) and `cancelOnReply`. Resolves with the stored scheduled message.
 */
export async function scheduleStaffReply({
    session, body = '', channel = null, subject = '', mediaUrl = null, contentSid = '', contentVariables = null, templateBody = '',
    sendingIdentityId = null, sendingIdentityExplicitlyChosen = false, orgId = '', recipientAddress = '', groupAddresses = null, clientRef = '',
    scheduledFor, cancelOnReply = false, client = supabaseClient,
} = {}) {
    const validationSession = session || {
        id: 'new-recipient', channel,
        contact_phone: channel === 'email' ? '' : (recipientAddress || (Array.isArray(groupAddresses) ? groupAddresses[0] || '' : '')),
        contact_email: channel === 'email' ? recipientAddress : '',
    };
    const validation = validateStaffReply({ session: validationSession, body, channel, contentSid, mediaUrl });
    if (!validation.valid) throw new Error(validation.error);
    const payload = buildStaffReplyPayload({
        session, body, channel, subject, mediaUrl, contentSid, contentVariables, templateBody, sendingIdentityId, sendingIdentityExplicitlyChosen, orgId, recipientAddress, groupAddresses, clientRef,
    });
    const at = scheduledFor instanceof Date ? scheduledFor.toISOString() : String(scheduledFor || '');
    const data = await invoke(client, { ...payload, action: 'create', scheduledFor: at, cancelOnReply: Boolean(cancelOnReply) });
    return data.scheduled;
}

export async function updateScheduledMessage(id, patch = {}, client = supabaseClient) {
    const body = { action: 'update', id };
    if (patch.body !== undefined) body.body = patch.body;
    if (patch.subject !== undefined) body.subject = patch.subject;
    if (patch.cancelOnReply !== undefined) body.cancelOnReply = Boolean(patch.cancelOnReply);
    if (patch.scheduledFor !== undefined) body.scheduledFor = patch.scheduledFor instanceof Date ? patch.scheduledFor.toISOString() : patch.scheduledFor;
    const data = await invoke(client, body);
    return data.scheduled;
}

export async function cancelScheduledMessage(id, client = supabaseClient) {
    return (await invoke(client, { action: 'cancel', id })).scheduled;
}

/** Sends right now (the same checks and path as the scheduler). Throws with the reason when it could not go out. */
export async function sendScheduledNow(id, client = supabaseClient) {
    const data = await invoke(client, { action: 'send_now', id });
    return data;
}

const COLUMNS = 'id, org_id, session_id, created_by, channel, recipient_address, group_addresses, subject, body, media_urls, content_sid, scheduled_for, '
    + 'company_time_zone, recipient_time_zone, cancel_on_reply, status, status_reason, waiting_until, sent_at, client_ref, created_at, updated_at';

/**
 * Scheduled messages the signed-in person can see, newest due first. `sessionId` limits to one conversation;
 * `statuses` defaults to what still matters on screen (waiting to go, going now, or stopped with a reason).
 */
export async function loadScheduledMessages({ client = supabaseClient, sessionId = null, statuses = ['scheduled', 'sending', 'failed', 'cancelled', ...(sessionId ? ['sent'] : [])], sinceHours = 72, limit = 200 } = {}) {
    let query = client.from('scheduled_messages').select(COLUMNS).in('status', statuses).order('scheduled_for', { ascending: true }).limit(limit);
    if (sessionId) query = query.eq('session_id', sessionId);
    if (statuses.some((status) => status !== 'scheduled' && status !== 'sending')) {
        // failed / cancelled rows stay visible for a while so the reason is seen, then fade away
        const since = new Date(Date.now() - sinceHours * 3600000).toISOString();
        query = query.or(`status.in.(scheduled,sending),updated_at.gte.${since}`);
    }
    const { data, error } = await query;
    if (error) throw error;
    return Array.isArray(data) ? data : [];
}

export async function loadScheduledEvents(id, client = supabaseClient) {
    const { data, error } = await client.from('scheduled_message_events').select('id, event, actor_id, detail, created_at')
        .eq('scheduled_message_id', id).order('created_at', { ascending: true });
    if (error) throw error;
    return Array.isArray(data) ? data : [];
}

/**
 * The company's allowed delivery hours ('08:00'-'21:00' when nothing is set).
 * BL-177 Step 2j: NOT used for staff messages any more (no quiet-hours hold, no admin setting); kept with org_scheduled_send_settings for the
 * future SEO / Marketing module, where quiet hours will apply only to Campaigns / marketing sends.
 */
export async function loadQuietHours(orgId, client = supabaseClient) {
    if (!orgId) return null;
    const { data } = await client.from('org_scheduled_send_settings').select('quiet_start, quiet_end').eq('org_id', orgId).maybeSingle();
    return data && data.quiet_start && data.quiet_end ? { start: String(data.quiet_start).slice(0, 5), end: String(data.quiet_end).slice(0, 5) } : null;
}

export async function saveQuietHours(orgId, { start, end }, client = supabaseClient) {
    const { error } = await client.from('org_scheduled_send_settings').upsert({ org_id: orgId, quiet_start: start, quiet_end: end }, { onConflict: 'org_id' });
    if (error) throw error;
    announceChange();
}
